/**
 * Typed client for the two endpoints this screen consumes.
 *
 * Shapes are transcribed from the service's Pydantic models, not guessed. The API is
 * snake_case everywhere *except* inside `submissions[].request` (the UTURN payload) and
 * the resolved-location match objects, which are dumped `by_alias=True` and are
 * therefore camelCase. Both casings appear below, deliberately.
 */

/**
 * Absolute base URL of the backend. The app calls it directly — there is no proxy — so
 * this origin must be allowed by the backend's CORS configuration (see README.md).
 *
 * Configured with `VITE_DOCUMENT_READER_API_BASE_URL`; an empty or unset value falls
 * through to the development deployment. Any trailing slash is trimmed so
 * `${API_BASE}${path}` is clean.
 */
const DEFAULT_API_BASE_URL =
	'https://document-reader-development.up.railway.app';

function resolveApiBase(): string {
	const configured =
		import.meta.env.VITE_DOCUMENT_READER_API_BASE_URL?.trim() ?? '';
	const base = configured === '' ? DEFAULT_API_BASE_URL : configured;
	return base.replace(/\/+$/, '');
}

const API_BASE = resolveApiBase();

/** Hard cap enforced by the backend (`Settings.max_upload_bytes`, default 20 MiB). */
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

/** Extensions the intake layer can read (`intake/detect.py`). */
export const ACCEPTED_EXTENSIONS = ['.pdf', '.csv', '.xlsx', '.docx'] as const;

/**
 * The submit request itself is given more headroom than the backend's worst case, so a
 * slow-but-succeeding extraction is never aborted by the client.
 */
const SUBMIT_TIMEOUT_MS = 180_000;
const READ_TIMEOUT_MS = 30_000;

// ---------------------------------------------------------------------------
// Extraction tree (snake_case) — extraction/models.py
// ---------------------------------------------------------------------------

/**
 * One value the LLM extracted, with confidence and provenance.
 *
 * Contract: when the document does not contain the value, `value` is null and
 * `confidence` is 0.0 — so a 0 % here means "absent", not "badly read".
 */
export interface ExtractedField<T> {
	value: T | null;
	confidence: number;
	source_text: string | null;
}

export type StopType = 'pickup' | 'intermediate' | 'drop';

export interface ExtractedContainer {
	container_type: ExtractedField<string>;
	iso_type: ExtractedField<string>;
	status: ExtractedField<string>;
	shipping_line: ExtractedField<string>;
	container_number: ExtractedField<string>;
	tare_weight: ExtractedField<string>;
	generator_set: ExtractedField<boolean>;
	seal_number: ExtractedField<string>;
}

export interface ExtractedCargo {
	gross_weight: ExtractedField<string>;
	un_code: ExtractedField<string>;
	description: ExtractedField<string>;
	requirements: ExtractedField<string[]>;
	additional_info: ExtractedField<string>;
}

/**
 * `stop_type` is the coarse enum (pickup/intermediate/drop) — it carries no confidence, and
 * collapses e.g. `DELIVER` into `drop`. The finer action (`DELIVER` vs `DROP`) lives on the
 * index-aligned `request_json.locationActions[]`, which is preferred for the header.
 */
export interface ExtractedRouteStop {
	stop_type: StopType;
	location: ExtractedField<string>;
	window_from: ExtractedField<string>;
	window_to: ExtractedField<string>;
	reference: ExtractedField<string>;
	remarks: ExtractedField<string>;
}

export interface ExtractedOrder {
	container: ExtractedContainer;
	cargo: ExtractedCargo;
	stops: ExtractedRouteStop[];
	shipper_reference: ExtractedField<string>;
	conditions: ExtractedField<string>;
	quantity: ExtractedField<number>;
	notes: string | null;
	overall_confidence: number;
}

export interface ExtractedDocument {
	document_type: string;
	language: string;
	carrier: string | null;
	shipper: ExtractedField<string>;
	orders: ExtractedOrder[];
	total_price: ExtractedField<string>;
	notes: string | null;
	overall_confidence: number;
}

// ---------------------------------------------------------------------------
// Validation (snake_case) — validation/models.py
// ---------------------------------------------------------------------------

export type Severity = 'error' | 'warning' | 'info';

export interface ValidationIssue {
	/** Dotted path with array indices, e.g. `container.container_number`, `stops[0].window_from`. Empty for shipment-wide issues. */
	field_path: string;
	code: string;
	severity: Severity;
	message: string;
}

export interface OrderValidation {
	is_valid: boolean;
	issues: ValidationIssue[];
	error_count: number;
	warning_count: number;
}

// ---------------------------------------------------------------------------
// UTURN API shapes (camelCase — model_dump(by_alias=True)) — uturn_api/models.py
// ---------------------------------------------------------------------------

export interface UturnLocationAction {
	locationActionType: string;
	sequenceNumber: number;
	location: { id?: number | null; googlePlaceId?: string | null };
	dateFrom: string | null;
	dateUntil: string | null;
	reference: string | null;
	remarks: string | null;
}

/** The payload actually pushed to UTURN. Only the parts this screen reads are typed. */
export interface UturnRequest {
	equipment: {
		shipmentUnitId: number;
		isoType: string | null;
		containerNumber: string | null;
	};
	grossWeight: { quantity: number; unit: string };
	seal: string | null;
	shipperReference: string | null;
	shippingLineId: number | null;
	locationActions: UturnLocationAction[];
	numberOfCopiesToBeCreated: number;
	requirementIds: number[];
	price: { quantity: number; currencyCode: string };
	conditions: string | null;
	cargoDescription: string | null;
	details: string | null;
}

/** Our normalized view of a `createShipment` result. `ok === false` arrives inside a 200. */
export interface OrderApiResponse {
	status_code: number;
	ok: boolean;
	shipment_id: string | null;
	errors: string[];
}

export interface LocationMatch {
	name: string;
	placeId: string | null;
	detailsHidden: boolean;
	source: string;
}

/**
 * One resolved stop. `fallback*` keys are **absent** in the normal case, present only
 * when the address could not be geocoded and the placeholder search text was used.
 */
export interface ResolvedLocation {
	stop_type: StopType;
	address: string | null;
	query: string | null;
	chosen: LocationMatch | null;
	candidates: LocationMatch[];
	fallback?: true;
	fallback_query?: string;
	fallback_chosen?: LocationMatch;
}

// ---------------------------------------------------------------------------
// POST /documents/submit
// ---------------------------------------------------------------------------

/** `submissions[i]` is index-aligned with `extracted_document.orders[i]`. */
export interface SubmissionEntry {
	/** Null when the order failed before it could be mapped. */
	request: UturnRequest | null;
	validation: OrderValidation;
	locations: ResolvedLocation[];
	api_response: OrderApiResponse;
}

export interface DuplicateOf {
	submission_id: string;
	customer_key: string | null;
	filename: string;
	created_at: string;
	order_count: number;
}

export interface TokenUsage {
	model: string;
	input_tokens: number;
	output_tokens: number;
	total_tokens: number;
	estimated_cost_usd: number;
}

export interface SubmitResponse {
	filename: string;
	format: string;
	char_count: number;
	/** "simulated" means nothing reached UTURN and the ids are locally generated FAKE-... values. */
	uturn_mode: 'real' | 'simulated';
	order_count: number;
	accepted_count: number;
	customer_key: string | null;
	/** Null when STORE_EXTRACTIONS=false, or when best-effort persistence failed. */
	submission_id: string | null;
	duplicate_of: DuplicateOf | null;
	total_price: string | null;
	price_per_container: { quantity: number; currencyCode: string } | null;
	token_usage: TokenUsage;
	submissions: SubmissionEntry[];
}

// ---------------------------------------------------------------------------
// GET /storage/submissions/{submission_id}
// ---------------------------------------------------------------------------

export interface StoredOrder {
	submitted_order_id: string;
	order_index: number;
	container_number: string | null;
	uturn_shipment_number: string | null;
	api_ok: boolean;
	api_errors: string[];
	/** The camelCase payload pushed to UTURN; `locationActions` carries the per-stop action type. */
	request_json: UturnRequest | null;
}

export interface StoredSubmission {
	submission_id: string;
	customer_key: string | null;
	customer_alias: string | null;
	filename: string;
	document_sha256: string;
	status: string;
	created_at: string;
	updated_at: string;
	source_document_ref: string | null;
	extracted_document: ExtractedDocument | null;
	orders: StoredOrder[];
}

/**
 * The read-back is wrapped. With STORE_EXTRACTIONS=false this comes back as a **200**
 * with `store_extractions: false` and `submission: null` — a 404 only means the id is
 * unknown while the store is on.
 */
export interface StorageResponse {
	store_extractions: boolean;
	submission: StoredSubmission | null;
}

// ---------------------------------------------------------------------------
// GET /health/config
// ---------------------------------------------------------------------------

export interface HealthConfig {
	uturn_mode: 'real' | 'simulated';
	store_extractions: boolean;
	llm_model: string;
	storage_mode: string;
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

export class ApiError extends Error {
	readonly status: number;

	constructor(status: number, message: string) {
		super(message);
		this.name = 'ApiError';
		this.status = status;
	}
}

/**
 * Returns the bearer token for the current request, or null when there is none.
 *
 * A **getter**, deliberately, not a token: the Clerk session token is only valid for
 * 60 seconds, so anything held in a variable is expired well before the next upload.
 * It is resolved immediately before every `fetch`, never reused.
 */
type TokenProvider = () => Promise<string | null>;

let tokenProvider: TokenProvider | null = null;

/**
 * Register (or with `null`, clear) the source of the `Authorization` header.
 *
 * This module stays free of React and every call site stays unchanged; `auth/TokenBridge`
 * is the only caller. While no provider is registered — the auth-disabled case — requests
 * go out exactly as they did before sign-in existed.
 */
export function setTokenProvider(provider: TokenProvider | null): void {
	tokenProvider = provider;
}

/** Merge the bearer token into `init` without disturbing anything else the caller set. */
async function withAuth(init: RequestInit): Promise<RequestInit> {
	if (tokenProvider === null) return init;

	let token: string | null;
	try {
		token = await tokenProvider();
	} catch {
		token = null;
	}

	// Auth is on, so an unauthenticated request is not a useful fallback — it would reach
	// UTURN as the wrong identity once the backend starts forwarding this header.
	if (token === null || token === '') {
		throw new ApiError(
			401,
			'Your session has expired. Sign in again and try once more.',
		);
	}

	// Copy the caller's headers and set exactly one. `submitDocument` leaves `Content-Type`
	// unset on purpose so the browser can add the multipart boundary — replacing the header
	// set, or writing a content type by hand, silently breaks every upload.
	const headers = new Headers(init.headers);
	headers.set('Authorization', `Bearer ${token}`);
	return { ...init, headers };
}

/**
 * Pull the human-readable message out of a failed response.
 *
 * The service's 413/415/501/422/502 bodies are `{"detail": "<sentence>"}` and those
 * sentences are written to be shown to a user, so the string is returned **verbatim**.
 * A missing `file` part instead yields FastAPI's own validation array, which is joined.
 */
async function readErrorDetail(res: Response): Promise<string> {
	const fallback = `HTTP ${res.status}${res.statusText ? ` ${res.statusText}` : ''}`;

	let body: unknown;
	try {
		body = await res.json();
	} catch {
		return fallback;
	}

	const detail = (body as { detail?: unknown } | null)?.detail;

	if (typeof detail === 'string' && detail.trim() !== '') return detail;

	if (Array.isArray(detail)) {
		const messages = detail
			.map((item) =>
				item !== null && typeof item === 'object' && 'msg' in item
					? String((item as { msg: unknown }).msg)
					: JSON.stringify(item),
			)
			.filter((msg) => msg !== '');
		if (messages.length > 0) return messages.join(' · ');
	}

	if (detail != null) return JSON.stringify(detail);
	return fallback;
}

async function request<T>(
	path: string,
	init: RequestInit,
	timeoutMs: number,
): Promise<T> {
	// Resolved before the timer starts, so fetching a token never eats into the request's
	// own budget — a submit already needs most of its 180 seconds.
	const authed = await withAuth(init);

	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), timeoutMs);

	let res: Response;
	try {
		res = await fetch(`${API_BASE}${path}`, {
			...authed,
			signal: controller.signal,
		});
	} catch (error) {
		if (error instanceof DOMException && error.name === 'AbortError') {
			throw new ApiError(
				0,
				`The request took longer than ${Math.round(timeoutMs / 1000)} seconds and was aborted.`,
			);
		}
		throw new ApiError(
			0,
			'Cannot reach the server. Is the backend running and is CORS configured correctly?',
		);
	} finally {
		clearTimeout(timer);
	}

	// A 401 is about the session, not about this document, so it gets its own sentence
	// rather than whatever the service put in `detail`. The backend does not check the
	// token yet, so today this is not expected — it will once its half lands.
	if (res.status === 401) {
		throw new ApiError(
			401,
			'Your session has expired or is invalid. Sign in again and try once more.',
		);
	}

	if (!res.ok) throw new ApiError(res.status, await readErrorDetail(res));
	return (await res.json()) as T;
}

/** Upload a document and push its containers to UTURN. Multipart part name is `file`. */
export function submitDocument(file: File): Promise<SubmitResponse> {
	const form = new FormData();
	form.append('file', file);
	return request<SubmitResponse>(
		'/documents/submit',
		{ method: 'POST', body: form },
		SUBMIT_TIMEOUT_MS,
	);
}

/** Read back the persisted extraction, which is where confidence and source text live. */
export function fetchSubmission(
	submissionId: string,
): Promise<StorageResponse> {
	return request<StorageResponse>(
		`/storage/submissions/${encodeURIComponent(submissionId)}`,
		{ method: 'GET' },
		READ_TIMEOUT_MS,
	);
}

/** Effective server configuration. Advisory only — a failure here must not block the screen. */
export function fetchHealthConfig(): Promise<HealthConfig> {
	return request<HealthConfig>(
		'/health/config',
		{ method: 'GET' },
		READ_TIMEOUT_MS,
	);
}
